import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Clock, Ghost } from "lucide-react";

const FILTERS = [
  { id: "all", label: "ALL DROPS" },
  { id: "halloween", label: "HALLOWEEN DROP" },
];

const ComingSoonPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/products?coming_soon=true")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const hasHalloween = useMemo(() => items.some((p) => p.halloween_drop), [items]);
  const visible = useMemo(() => {
    if (filter === "halloween") return items.filter((p) => p.halloween_drop);
    return items;
  }, [items, filter]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="coming-soon-page">
        <div className="border-b border-zinc-800 pb-8 mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-[1px] bg-zinc-500" />
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 uppercase flex items-center gap-2">
              <Clock className="w-3 h-3" /> [ 01 · NEXT DROP · IN THE LAB ]
            </div>
          </div>
          <h1 className="display-h1 text-5xl sm:text-7xl text-zinc-50">
            Coming <span className="text-zinc-400">Soon</span>
          </h1>
          <p className="mt-4 max-w-2xl font-mono-tech text-[11px] uppercase tracking-[0.25em] text-zinc-400">
            // Sneak peek at what&apos;s dropping next. Not yet available for purchase — stay locked.
          </p>
        </div>

        {hasHalloween && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="coming-soon-filter-row">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const isHalloween = f.id === "halloween";
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  className={`font-mono-tech text-xs uppercase tracking-widest px-4 py-2 border-2 transition-all inline-flex items-center gap-2 ${
                    active
                      ? isHalloween
                        ? "bg-zinc-300 border-zinc-300 text-zinc-950"
                        : "bg-zinc-100 border-zinc-100 text-zinc-950"
                      : isHalloween
                        ? "border-zinc-300 text-zinc-200 hover:bg-zinc-300 hover:text-zinc-950"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
                  }`}
                >
                  {isHalloween && <Ghost className="w-3.5 h-3.5" />}
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="text-center py-24 font-mono-tech text-zinc-500 uppercase text-sm">// Loading upcoming drops ...</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-zinc-800" data-testid="coming-soon-empty">
            <div className="font-display text-3xl text-zinc-500 uppercase">Nothing in the lab right now</div>
            <div className="mt-3 font-mono-tech text-xs text-zinc-600 uppercase tracking-widest">// Check back for the next drop.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coming-soon-grid">
            {visible.map((p) => (
              <div
                key={p.id}
                data-testid={`coming-soon-card-${p.slug}`}
                className="group border-2 border-zinc-800 bg-zinc-950 relative"
              >
                <span className="absolute top-2 left-2 z-10 bg-zinc-200 text-zinc-950 font-mono-tech text-[10px] px-2 py-1 uppercase tracking-widest">Coming Soon</span>
                {p.halloween_drop && (
                  <span
                    className="absolute top-2 right-2 z-10 bg-zinc-300 text-zinc-950 font-mono-tech text-[10px] px-2 py-1 uppercase tracking-widest inline-flex items-center gap-1"
                    data-testid={`halloween-badge-${p.slug}`}
                  >
                    <Ghost className="w-3 h-3" /> Halloween
                  </span>
                )}
                <div className="aspect-square bg-zinc-900 overflow-hidden relative">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover filter grayscale-[45%] group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent pointer-events-none" />
                </div>
                <div className="p-4 border-t-2 border-zinc-800">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <div className="font-mono-tech text-xs text-zinc-500 uppercase">{p.category}</div>
                      <div className="font-display text-xl text-zinc-50 mt-1 uppercase tracking-wider">{p.name}</div>
                    </div>
                    <div className="font-mono-tech text-sm text-zinc-200 whitespace-nowrap">${p.price.toFixed(2)}</div>
                  </div>
                  <div className="mt-3 font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest">// Dropping soon</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link
            to="/shop"
            className="inline-block font-mono-tech text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-300 text-zinc-200 hover:bg-zinc-300 hover:text-zinc-950 transition-colors"
            data-testid="coming-soon-shop-link"
          >
            Shop Available Drops →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ComingSoonPage;
